import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo } from "firebase/database";
import { getAuth } from 'firebase/auth';
import './NodeConfigSidebar.css';

const NodeConfigSidebar = ({ isOpen, selectedNode, onClose, onSave }) => {
    const [config, setConfig] = useState({});
    const [emailFlows, setEmailFlows] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Load config when node changes
    useEffect(() => {
        if (selectedNode) {
            setConfig(selectedNode.data.config || {});
        }
    }, [selectedNode]);

    // Load email flows and templates
    useEffect(() => {
        if (!currentUser || !isOpen) return;

        setLoading(true);

        // Load email flows
        const flowsQuery = query(
            ref(db, 'emailFlows'),
            orderByChild('userId'),
            equalTo(currentUser.uid)
        );

        const templatesQuery = query(
            ref(db, 'emailTemplates'),
            orderByChild('userId'),
            equalTo(currentUser.uid)
        );

        // Load flows
        onValue(flowsQuery, (snapshot) => {
            if (snapshot.exists()) {
                const flowsData = [];
                snapshot.forEach((childSnapshot) => {
                    flowsData.push({
                        id: childSnapshot.key,
                        ...childSnapshot.val()
                    });
                });
                setEmailFlows(flowsData.filter(flow => flow.status === 'active'));
            } else {
                setEmailFlows([]);
            }
        });

        // Load templates
        onValue(templatesQuery, (snapshot) => {
            if (snapshot.exists()) {
                const templatesData = [];
                snapshot.forEach((childSnapshot) => {
                    templatesData.push({
                        id: childSnapshot.key,
                        ...childSnapshot.val()
                    });
                });
                setEmailTemplates(templatesData);
            } else {
                setEmailTemplates([]);
            }
            setLoading(false);
        });

    }, [currentUser, db, isOpen]);

    const handleConfigChange = (newConfig) => {
        setConfig(newConfig);
        // Auto-save on change
        if (onSave && selectedNode) {
            onSave(selectedNode.id, { ...selectedNode.data, config: newConfig });
        }
    };

    const handleTemplateSelect = (template) => {
        const newConfig = {
            ...config,
            selectedTemplateId: template.id,
            templateName: template.name,
            sendDelay: config.sendDelay || { amount: 0, unit: 'minutes' }
        };
        handleConfigChange(newConfig);
    };

    const handleFlowSelect = (flow) => {
        const newConfig = {
            ...config,
            selectedFlowId: flow.id,
            flowName: flow.name,
            triggerDelay: config.triggerDelay || { amount: 0, unit: 'minutes' }
        };
        handleConfigChange(newConfig);
    };

    const handleDelayChange = (field, value) => {
        const delayField = selectedNode?.data.actionType === 'start_email_flow' ? 'triggerDelay' : 'sendDelay';
        const newConfig = {
            ...config,
            [delayField]: {
                ...config[delayField],
                [field]: value
            }
        };
        handleConfigChange(newConfig);
    };

    const handleWebhookChange = (url) => {
        const newConfig = {
            ...config,
            webhookUrl: url
        };
        handleConfigChange(newConfig);
    };

    const handleScheduleChange = (time) => {
        const newConfig = {
            ...config,
            scheduleTime: time
        };
        handleConfigChange(newConfig);
    };

    const handleDelayConfigChange = (amount, unit) => {
        const newConfig = {
            ...config,
            delayAmount: amount,
            delayUnit: unit
        };
        handleConfigChange(newConfig);
    };

    if (!selectedNode) return null;

    const actionType = selectedNode.data.actionType;

    return (
        <div className={`node-config-sidebar ${isOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
                <div className="sidebar-title">
                    <span className="sidebar-icon">
                        {actionType === 'send_email' && '📧'}
                        {actionType === 'start_email_flow' && '⚡'}
                        {actionType === 'send_webhook' && '🔗'}
                        {actionType === 'delay' && '⏰'}
                        {actionType === 'schedule' && '📅'}
                    </span>
                    <h3>
                        {actionType === 'send_email' && 'Email Configuration'}
                        {actionType === 'start_email_flow' && 'Email Flow Configuration'}
                        {actionType === 'send_webhook' && 'Webhook Configuration'}
                        {actionType === 'delay' && 'Delay Configuration'}
                        {actionType === 'schedule' && 'Schedule Configuration'}
                    </h3>
                </div>
                <button className="sidebar-close" onClick={onClose}>×</button>
            </div>

            <div className="sidebar-content">
                {loading ? (
                    <div className="sidebar-loading">
                        <div className="loading-spinner"></div>
                        <p>Loading configuration...</p>
                    </div>
                ) : (
                    <>
                        {actionType === 'send_email' && (
                            <div className="config-section">
                                <h4>Email Template</h4>
                                <div className="template-list">
                                    {emailTemplates.length === 0 ? (
                                        <div className="no-items">
                                            <p>No email templates found.</p>
                                            <a href="/emailflow" target="_blank" rel="noopener noreferrer">
                                                Create Template →
                                            </a>
                                        </div>
                                    ) : (
                                        emailTemplates.map(template => (
                                            <div
                                                key={template.id}
                                                className={`template-item ${config.selectedTemplateId === template.id ? 'selected' : ''}`}
                                                onClick={() => handleTemplateSelect(template)}
                                            >
                                                <div className="template-header">
                                                    <h5>{template.name}</h5>
                                                    {config.selectedTemplateId === template.id && (
                                                        <span className="selected-badge">✓</span>
                                                    )}
                                                </div>
                                                <p>{template.description || 'No description'}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="config-section">
                                    <h4>Send Delay</h4>
                                    <div className="delay-controls">
                                        <input
                                            type="number"
                                            min="0"
                                            value={config.sendDelay?.amount || 0}
                                            onChange={(e) => handleDelayChange('amount', parseInt(e.target.value))}
                                            className="delay-input"
                                        />
                                        <select
                                            value={config.sendDelay?.unit || 'minutes'}
                                            onChange={(e) => handleDelayChange('unit', e.target.value)}
                                            className="delay-select"
                                        >
                                            <option value="minutes">Minutes</option>
                                            <option value="hours">Hours</option>
                                            <option value="days">Days</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {actionType === 'start_email_flow' && (
                            <div className="config-section">
                                <h4>Email Flow</h4>
                                <div className="flow-list">
                                    {emailFlows.length === 0 ? (
                                        <div className="no-items">
                                            <p>No active email flows found.</p>
                                            <a href="/emailflow" target="_blank" rel="noopener noreferrer">
                                                Create Flow →
                                            </a>
                                        </div>
                                    ) : (
                                        emailFlows.map(flow => (
                                            <div
                                                key={flow.id}
                                                className={`flow-item ${config.selectedFlowId === flow.id ? 'selected' : ''}`}
                                                onClick={() => handleFlowSelect(flow)}
                                            >
                                                <div className="flow-header">
                                                    <h5>{flow.name}</h5>
                                                    <span className="flow-badge">{flow.emailCount || 0} emails</span>
                                                    {config.selectedFlowId === flow.id && (
                                                        <span className="selected-badge">✓</span>
                                                    )}
                                                </div>
                                                <p>{flow.description || 'No description'}</p>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <div className="config-section">
                                    <h4>Trigger Delay</h4>
                                    <div className="delay-controls">
                                        <input
                                            type="number"
                                            min="0"
                                            value={config.triggerDelay?.amount || 0}
                                            onChange={(e) => handleDelayChange('amount', parseInt(e.target.value))}
                                            className="delay-input"
                                        />
                                        <select
                                            value={config.triggerDelay?.unit || 'minutes'}
                                            onChange={(e) => handleDelayChange('unit', e.target.value)}
                                            className="delay-select"
                                        >
                                            <option value="minutes">Minutes</option>
                                            <option value="hours">Hours</option>
                                            <option value="days">Days</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {actionType === 'send_webhook' && (
                            <div className="config-section">
                                <h4>Webhook URL</h4>
                                <input
                                    type="url"
                                    placeholder="https://your-webhook-url.com/endpoint"
                                    value={config.webhookUrl || ''}
                                    onChange={(e) => handleWebhookChange(e.target.value)}
                                    className="webhook-input"
                                />
                                <p className="config-help">
                                    Data will be sent to this URL when the trigger fires
                                </p>
                            </div>
                        )}

                        {actionType === 'delay' && (
                            <div className="config-section">
                                <h4>Delay Duration</h4>
                                <div className="delay-controls">
                                    <input
                                        type="number"
                                        min="1"
                                        value={config.delayAmount || 1}
                                        onChange={(e) => handleDelayConfigChange(parseInt(e.target.value), config.delayUnit)}
                                        className="delay-input"
                                    />
                                    <select
                                        value={config.delayUnit || 'hours'}
                                        onChange={(e) => handleDelayConfigChange(config.delayAmount, e.target.value)}
                                        className="delay-select"
                                    >
                                        <option value="minutes">Minutes</option>
                                        <option value="hours">Hours</option>
                                        <option value="days">Days</option>
                                        <option value="weeks">Weeks</option>
                                    </select>
                                </div>
                                <p className="config-help">
                                    Wait {config.delayAmount || 1} {config.delayUnit || 'hours'} before next action
                                </p>
                            </div>
                        )}

                        {actionType === 'schedule' && (
                            <div className="config-section">
                                <h4>Schedule Time</h4>
                                <input
                                    type="datetime-local"
                                    value={config.scheduleTime || ''}
                                    onChange={(e) => handleScheduleChange(e.target.value)}
                                    className="schedule-input"
                                />
                                <p className="config-help">
                                    Action will be executed at the specified time
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default NodeConfigSidebar;
